import { uiText } from "@/lib/i18n/uiText";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Rendered instead of the children when the language UI throws. */
  fallback?: ReactNode;
}

/**
 * Isolates the language UI (switcher, locale-aware chrome) from the rest of the
 * app. If i18n or the locale context misbehaves, only this small subtree is
 * replaced by an inert fallback — the page never goes blank.
 */
export class LanguageErrorBoundary extends Component<Props, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep this quiet in production but traceable in dev/telemetry.
    console.warn("[i18n] language UI failed, rendering fallback", error, info.componentStack);
  }

  override render() {
    if (this.state.failed) {
      return (
        this.props.fallback ?? (
          <span className="px-2 text-xs uppercase text-muted-foreground" aria-hidden="true">
             {uiText("EN")} </span>
        )
      );
    }
    return this.props.children;
  }
}
